import React, { useEffect, useState } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiSearch, FiEye, FiRefreshCw, FiTrendingUp, FiUsers, FiPlus } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import PSODetailsModal from '../components/PSODetailsModal';
import DeactivationModal from '../components/DeactivationModal';
import AddStaffModal from '../components/AddStaffModal';
import { fetchPSOs, updatePSO, type PSO } from '../service/psoService';
import { fetchBranchBankId } from '../../branches/service/branchesService';
import Table, { Column } from '../../../components/Table';

export default function PSO() {
    const [psos, setPsos] = useState<PSO[]>([]);
    const [filtered, setFiltered] = useState<PSO[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedPSO, setSelectedPSO] = useState<PSO | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [psoToDeactivate, setPsoToDeactivate] = useState<PSO | null>(null);
    const [loading, setLoading] = useState(true);
    const [bankId, setBankId] = useState<string | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [designations, setDesignations] = useState<string[]>([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                let fetchedBankId: string | null = null;

                if (isBankUser) {
                    // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
                    const { getDoc, doc } = await import('firebase/firestore');
                    const { db } = await import('../../../firebase/config');
                    const directBank = await getDoc(doc(db, "banks", uid));
                    if (directBank.exists()) {
                        fetchedBankId = uid;
                    } else {
                        // Try query by userId field
                        const { getDocs, collection } = await import('firebase/firestore');
                        const banksSnap = await getDocs(collection(db, "banks"));
                        for (const bankDoc of banksSnap.docs) {
                            const bankData = bankDoc.data();
                            if (bankData.userId === uid) {
                                fetchedBankId = bankDoc.id;
                                break;
                            }
                        }
                    }
                } else {
                    // For PSO users, find the bankId
                    fetchedBankId = await fetchBranchBankId(uid);
                }

                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const psosData = await fetchPSOs(fetchedBankId);
                    setPsos(psosData);

                    // Extract unique designations
                    const designationSet = new Set<string>();
                    psosData.forEach(pso => {
                        if (pso.designation) {
                            designationSet.add(pso.designation);
                        }
                    });
                    setDesignations(Array.from(designationSet));

                    // Filtered list will be set by useEffect
                }
            } catch (err: any) {
                showToast('Failed to load PSOs data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = psos;

        // Apply filter based on selected designation
        if (selectedFilter && selectedFilter !== 'All') {
            if (selectedFilter === 'Head') {
                list = list.filter(pso =>
                    pso.designation?.toLowerCase().includes('head') &&
                    !pso.designation?.toLowerCase().includes('regional')
                );
            } else if (selectedFilter === 'Regional Head') {
                list = list.filter(pso => pso.designation?.toLowerCase().includes('regional'));
            } else if (selectedFilter === 'PSO') {
                list = list.filter(pso =>
                    !pso.designation ||
                    pso.designation?.toLowerCase() === 'pso' ||
                    (!pso.designation?.toLowerCase().includes('head') && !pso.designation?.toLowerCase().includes('regional'))
                );
            } else {
                list = list.filter(pso => pso.designation === selectedFilter);
            }
        }

        // Apply search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            list = list.filter(
                (pso) =>
                    pso.name?.toLowerCase().includes(term) ||
                    pso.contact?.toLowerCase().includes(term) ||
                    pso.email?.toLowerCase().includes(term)
            );
        }

        // Sort by name alphabetically
        list.sort((a, b) => {
            const nameA = (a.name || '').toString();
            const nameB = (b.name || '').toString();
            return nameA.localeCompare(nameB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, psos, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(psos);
    };

    const handleStatCardClick = (designation: string) => {
        setSelectedFilter(selectedFilter === designation ? null : designation);
    };

    const openDetailsModal = (pso: PSO) => {
        setSelectedPSO(pso);
        setShowDetailsModal(true);
    };

    const handleStatusToggle = (pso: PSO) => {
        // If PSO is active, show deactivation modal
        if (pso.isActive !== false) {
            setPsoToDeactivate(pso);
            setShowDeactivationModal(true);
        } else {
            // If PSO is inactive, reactivate directly
            handleReactivate(pso);
        }
    };

    const handleDeactivate = async (reason: string, date: string) => {
        if (!psoToDeactivate || !bankId) return;

        setLoading(true);
        try {
            const psoRef = doc(db, 'banks', bankId, 'pos', psoToDeactivate.id);
            await updateDoc(psoRef, {
                isActive: false,
                deactivationReason: reason,
                deactivationDate: date,
                updatedAt: new Date()
            });

            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            setShowDeactivationModal(false);
            setPsoToDeactivate(null);
            showToast('✅ PSO deactivated successfully!', 'success');
        } catch (err: any) {
            console.error('Error deactivating PSO:', err);
            showToast('❌ Failed to deactivate PSO', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReactivate = async (pso: PSO) => {
        if (!bankId) return;

        setLoading(true);
        try {
            const psoRef = doc(db, 'banks', bankId, 'pos', pso.id);
            await updateDoc(psoRef, {
                isActive: true,
                deactivationReason: null,
                deactivationDate: null,
                updatedAt: new Date()
            });

            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            showToast('✅ PSO reactivated successfully!', 'success');
        } catch (err: any) {
            console.error('Error reactivating PSO:', err);
            showToast('❌ Failed to reactivate PSO', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStaffAdded = async () => {
        if (!bankId) return;

        setLoading(true);
        try {
            // Refresh data
            const psosData = await fetchPSOs(bankId);
            setPsos(psosData);

            // Extract unique designations
            const designationSet = new Set<string>();
            psosData.forEach(pso => {
                if (pso.designation) {
                    designationSet.add(pso.designation);
                }
            });
            setDesignations(Array.from(designationSet));

            setShowAddModal(false);
            showToast('✅ Staff added successfully!', 'success');
        } catch (err: any) {
            console.error('Error refreshing PSOs:', err);
            showToast('❌ Failed to refresh staff list', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading && psos.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading PSOs...</p>
                </div>
            </div>
        );
    }


    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const columns: Column<PSO>[] = [
        { header: 'Name', accessor: 'name', width: '25%', render: (pso) => <span className="font-medium text-gray-900 dark:text-gray-100">{pso.name || 'N/A'}</span> },
        { header: 'Contact', accessor: 'contact', width: '25%', className: 'text-gray-600 dark:text-gray-400' },
        { header: 'Email', accessor: 'email', width: '30%', className: 'text-gray-600 dark:text-gray-400' },
        {
            header: 'Designation',
            accessor: 'designation',
            width: '15%',
            render: (pso) => pso.designation ? (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {pso.designation}
                </span>
            ) : (
                <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
            )
        },
        {
            header: 'Status',
            width: '10%',
            align: 'center',
            render: (pso) => isBankUser ? (
                <div className="flex flex-col gap-2 items-center">
                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            checked={pso.isActive !== false}
                            onChange={() => handleStatusToggle(pso)}
                            className="peer sr-only"
                            disabled={loading || !bankId} // Disable if loading or no bankId
                        />
                        <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300 sr-only">
                            {pso.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                    </label>
                    {pso.isActive === false && pso.deactivationReason && (
                        <div className="mt-1 w-full">
                            <div
                                className="cursor-help text-justify"
                                title={`Reason: ${pso.deactivationReason}${pso.deactivationDate ? `\nDate: ${new Date(pso.deactivationDate).toLocaleDateString()}` : ''}`}
                            >
                                <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
                                    <strong>Reason:</strong> {pso.deactivationReason}
                                </p>
                                {pso.deactivationDate && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        <strong>Date:</strong> {new Date(pso.deactivationDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-2 items-center">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${pso.isActive !== false ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {pso.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                    {pso.isActive === false && pso.deactivationReason && (
                        <div className="mt-1 w-full">
                            <div
                                className="cursor-help text-justify"
                                title={`Reason: ${pso.deactivationReason}${pso.deactivationDate ? `\nDate: ${new Date(pso.deactivationDate).toLocaleDateString()}` : ''}`}
                            >
                                <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
                                    <strong>Reason:</strong> {pso.deactivationReason}
                                </p>
                                {pso.deactivationDate && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        <strong>Date:</strong> {new Date(pso.deactivationDate).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )
        },
        {
            header: 'Action',
            width: '10%',
            align: 'center',
            render: (pso) => (
                <button
                    onClick={() => openDetailsModal(pso)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    aria-label="View PSO details"
                >
                    <Icon icon={FiEye} className="h-4 w-4" />
                </button>
            )
        }
    ];

    const headerActions = isBankUser ? (
        <Button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2"
        >
            <Icon icon={FiPlus} className="h-4 w-4" />
            Add Staff
        </Button>
    ) : null;

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by name, contact, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            {searchTerm && (
                <Button
                    variant="secondary"
                    onClick={handleReset}
                    className="flex items-center gap-2"
                >
                    <Icon icon={FiRefreshCw} className="h-4 w-4" />
                    Reset
                </Button>
            )}
        </div>
    );

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Cards - Designations */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Total Staff Card (Non-clickable) */}
                    {(() => {
                        const activeCount = psos.filter(p => p.isActive !== false).length;
                        const inactiveCount = psos.filter(p => p.isActive === false).length;
                        return (
                            <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Staff</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{psos.length}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiUsers} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full w-full bg-gray-500 dark:bg-gray-500"
                                        title={`Total: ${psos.length} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Head Card */}
                    {(() => {
                        const headPSOs = psos.filter(p => p.designation?.toLowerCase().includes('head') && !p.designation?.toLowerCase().includes('regional'));
                        const count = headPSOs.length;
                        const activeCount = headPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = headPSOs.filter(p => p.isActive === false).length;
                        const percentage = psos.length > 0 ? (count / psos.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('Head')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'Head'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Head</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Regional Head Card */}
                    {(() => {
                        const regionalPSOs = psos.filter(p => p.designation?.toLowerCase().includes('regional'));
                        const count = regionalPSOs.length;
                        const activeCount = regionalPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = regionalPSOs.filter(p => p.isActive === false).length;
                        const percentage = psos.length > 0 ? (count / psos.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('Regional Head')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'Regional Head'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Head</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* PSO Card */}
                    {(() => {
                        const psoPSOs = psos.filter(p => !p.designation || p.designation?.toLowerCase() === 'pso' || (!p.designation?.toLowerCase().includes('head') && !p.designation?.toLowerCase().includes('regional')));
                        const count = psoPSOs.length;
                        const activeCount = psoPSOs.filter(p => p.isActive !== false).length;
                        const inactiveCount = psoPSOs.filter(p => p.isActive === false).length;
                        const percentage = psos.length > 0 ? (count / psos.length) * 100 : 0;
                        return (
                            <div
                                onClick={() => handleStatCardClick('PSO')}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === 'PSO'
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">PSO</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                            Active: {activeCount} | Inactive: {inactiveCount}
                                        </p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                        title={`Total: ${count} (Active: ${activeCount}, Inactive: ${inactiveCount})`}
                                    ></div>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                <Table
                    title={`Total Staff (${psos.length})`}
                    description="Manage and view all PSO information"
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(pso) => pso.id}
                    loading={loading}
                    emptyMessage={searchTerm || selectedFilter ? 'No PSOs found matching your search' : 'No PSOs available'}
                    headerActions={headerActions}
                    filters={filters}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />
            </div>

            {/* Modals */}
            <AddStaffModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleStaffAdded}
                bankId={bankId || undefined}
            />

            <DeactivationModal
                isOpen={showDeactivationModal}
                onClose={() => {
                    setShowDeactivationModal(false);
                    setPsoToDeactivate(null);
                }}
                onConfirm={handleDeactivate}
                psoName={psoToDeactivate?.name}
            />

            <PSODetailsModal
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedPSO(null);
                }}
                pso={selectedPSO}
                bankId={bankId || undefined}
                onUpdate={async (updatedData: Partial<PSO>) => {
                    if (!selectedPSO || !bankId) return;
                    try {
                        await updatePSO(bankId, selectedPSO.id, updatedData);
                        const psosData = await fetchPSOs(bankId);
                        setPsos(psosData);

                        // Extract unique designations
                        const designationSet = new Set<string>();
                        psosData.forEach(pso => {
                            if (pso.designation) {
                                designationSet.add(pso.designation);
                            }
                        });
                        setDesignations(Array.from(designationSet));

                        // Filtered list will be updated by useEffect
                        showToast('✅ PSO updated successfully!', 'success');
                        setShowDetailsModal(false);
                    } catch (err: any) {
                        throw err;
                    }
                }}
            />

            {/* Toast */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </>
    );
}

